import { useEffect } from "react";

export interface JsonLdProps {
  /**
   * Unique ID for the injected <script> tag.
   * If a tag with this ID already exists, it will be removed and replaced
   * when the data changes — preventing duplicate schema entries.
   */
  id: string;
  /**
   * Structured data object or array conforming to schema.org types.
   * Pass any valid JSON-LD payload (Organization, LocalBusiness, Event, etc.)
   */
  data: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * JsonLd
 *
 * Injects a `<script type="application/ld+json">` tag into `document.head`
 * for structured data / SEO. The tag is cleaned up on unmount and updated
 * reactively when `data` changes.
 *
 * Renders null — no DOM output, only the side-effect.
 *
 * @example
 * <JsonLd
 *   id="local-business-schema"
 *   data={{ "@context": "https://schema.org", "@type": "LocalBusiness", name: "My Studio" }}
 * />
 */
export function JsonLd({ id, data }: JsonLdProps) {
  const serialized = JSON.stringify(data);

  useEffect(() => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.textContent === serialized) return;
      existing.remove();
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.textContent = serialized;
    document.head.appendChild(script);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [id, serialized]);

  return null;
}
